import { NEURAL_CONFIG, AnimationUtils } from "./neural-config"
import { CameraController } from "./camera-controller"
import { NeuralSimulation } from "./neural-simulation"
import { VisualEffects } from "./visual-effects"

/**
 * Dynamic neural network renderer with optimized bloom and atmospheric effects
 */
export class SimpleRenderer {
  // Core Three.js dependencies
  private THREE: any
  private EffectComposer: any
  private RenderPass: any
  private UnrealBloomPass: any

  // Scene components
  public scene: any
  public camera: any
  public renderer: any
  public composer: any
  private bloomPass: any

  // Network data
  private network: any
  private params: any
  private clock: any

  // Visual elements
  private neuronMesh: any
  private neuronMaterial: any
  private dendriteMesh: any

  // Controllers
  private cameraController: CameraController | null = null
  private neuralSimulation: NeuralSimulation | null = null
  private visualEffects: VisualEffects | null = null

  // Performance tracking
  private performance = {
    frameCount: 0,
    lastTopologyUpdate: 0,
  }

  // Cleanup handlers
  private cleanupMouseEvents: (() => void) | null = null

  constructor(
    THREE: any,
    EffectComposer: any,
    RenderPass: any,
    UnrealBloomPass: any,
    network: any,
    params: any,
    clock: any,
  ) {
    this.THREE = THREE
    this.EffectComposer = EffectComposer
    this.RenderPass = RenderPass
    this.UnrealBloomPass = UnrealBloomPass
    this.network = network
    this.params = params
    this.clock = clock
  }

  // Helper method to create consistent hash from string
  private hashString(str: string): number {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return Math.abs(hash)
  }

  public async initializeRenderer(container: HTMLElement): Promise<void> {
    this.initializeScene()
    this.initializeCamera()
    this.initializeWebGLRenderer(container)
    this.setupMouseControls(container)
    this.initializePostProcessing()
    this.initializeLighting()

    // Initialize controllers
    this.cameraController = new CameraController(
      this.THREE,
      this.camera,
      this.network,
    )
    this.neuralSimulation = new NeuralSimulation(this.network)
    this.visualEffects = new VisualEffects(
      this.THREE,
      this.network,
      this.bloomPass,
    )
  }

  /**
   * Initialize the Three.js scene with atmospheric background
   */
  private initializeScene(): void {
    this.scene = new this.THREE.Scene()
    this.scene.background = new this.THREE.Color(NEURAL_CONFIG.BACKGROUND_COLOR)
    this.scene.fog = new this.THREE.Fog(
      NEURAL_CONFIG.FOG_COLOR,
      NEURAL_CONFIG.FOG_NEAR,
      NEURAL_CONFIG.FOG_FAR,
    )
  }

  /**
   * Initialize the perspective camera
   */
  private initializeCamera(): void {
    this.camera = new this.THREE.PerspectiveCamera(
      NEURAL_CONFIG.FOV,
      window.innerWidth / window.innerHeight,
      NEURAL_CONFIG.NEAR,
      NEURAL_CONFIG.FAR,
    )
    this.camera.position.set(
      NEURAL_CONFIG.INITIAL_POSITION.x,
      NEURAL_CONFIG.INITIAL_POSITION.y,
      NEURAL_CONFIG.INITIAL_POSITION.z,
    )
  }

  /**
   * Initialize the WebGL renderer with optimizations
   */
  private initializeWebGLRenderer(container: HTMLElement): void {
    this.renderer = new this.THREE.WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
    })
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio, NEURAL_CONFIG.MAX_PIXEL_RATIO),
    )
    this.renderer.toneMapping = this.THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.8
    this.renderer.setClearColor(NEURAL_CONFIG.BACKGROUND_COLOR, 1.0)
    container.appendChild(this.renderer.domElement)
  }

  /**
   * Initialize post-processing effects with bloom
   */
  private initializePostProcessing(): void {
    this.composer = new this.EffectComposer(this.renderer)

    const renderPass = new this.RenderPass(this.scene, this.camera)
    renderPass.clearColor = new this.THREE.Color(NEURAL_CONFIG.FOG_COLOR)
    renderPass.clearAlpha = 1.0
    this.composer.addPass(renderPass)

    this.bloomPass = new this.UnrealBloomPass(
      new this.THREE.Vector2(window.innerWidth, window.innerHeight),
      this.params.bloomStrength || 3.5,
      this.params.bloomRadius || 1.2,
      this.params.bloomThreshold || 0.03,
    )
    this.composer.addPass(this.bloomPass)
  }

  /**
   * Initialize atmospheric lighting setup
   */
  private initializeLighting(): void {
    const ambientLight = new this.THREE.AmbientLight(
      NEURAL_CONFIG.AMBIENT_LIGHT.color,
      NEURAL_CONFIG.AMBIENT_LIGHT.intensity,
    )
    this.scene.add(ambientLight)

    const directionalLight = new this.THREE.DirectionalLight(
      NEURAL_CONFIG.DIRECTIONAL_LIGHT.color,
      NEURAL_CONFIG.DIRECTIONAL_LIGHT.intensity,
    )
    directionalLight.position.set(100, 100, 100)
    this.scene.add(directionalLight)

    const fillLight = new this.THREE.DirectionalLight(
      NEURAL_CONFIG.FILL_LIGHT.color,
      NEURAL_CONFIG.FILL_LIGHT.intensity,
    )
    fillLight.position.set(-100, -50, 50)
    this.scene.add(fillLight)
  }

  /**
   * Setup mouse controls for interactive camera movement
   */
  private setupMouseControls(container: HTMLElement): void {
    const onMouseMove = (event: MouseEvent) => {
      const normalizedX = (event.clientX / window.innerWidth) * 2 - 1
      const normalizedY = -(event.clientY / window.innerHeight) * 2 + 1
      this.cameraController?.updateMousePosition(normalizedX, normalizedY)
    }

    const onMouseLeave = () => {
      this.cameraController?.deactivateMouseControl()
    }

    container.addEventListener("mousemove", onMouseMove)
    container.addEventListener("mouseleave", onMouseLeave)

    // Store cleanup function for disposal
    this.cleanupMouseEvents = () => {
      container.removeEventListener("mousemove", onMouseMove)
      container.removeEventListener("mouseleave", onMouseLeave)
    }
  }

  public createNeuronBodies(): void {
    // Create neuron geometries and materials

    // Remove existing neuron mesh if it exists
    if (this.neuronMesh) {
      this.scene.remove(this.neuronMesh)
    }

    // Use MeshLambertMaterial for dimensional lighting and depth
    const neuronGeometry = new this.THREE.SphereGeometry(
      this.params.neuronBodySize,
      NEURAL_CONFIG.NEURON_SEGMENTS.widthSegments,
      NEURAL_CONFIG.NEURON_SEGMENTS.heightSegments,
    )
    this.neuronMaterial = new this.THREE.MeshLambertMaterial({
      color: NEURAL_CONFIG.NEURON_COLOR, // Deeper blue base for better contrast
      transparent: false,
      opacity: 1.0,
      fog: true, // Explicitly enable fog response
      emissive: 0x002244, // Slightly more emissive blue
      emissiveIntensity: 0.8, // Increased emissive intensity for brighter glow
    })

    const neuronCount = this.network.neurons.length
    this.neuronMesh = new this.THREE.InstancedMesh(
      neuronGeometry,
      this.neuronMaterial,
      neuronCount,
    )
    this.neuronMesh.renderOrder = 0 // Render behind bloom layer

    // EXPLICITLY enable instance color attribute
    this.neuronMesh.instanceColor = new this.THREE.InstancedBufferAttribute(
      new Float32Array(neuronCount * 3),
      3,
    )

    // Set initial positions
    const matrix = new this.THREE.Matrix4()
    const color = new this.THREE.Color()

    for (let i = 0; i < neuronCount; i++) {
      const neuron = this.network.neurons[i]

      // Set position
      matrix.setPosition(
        neuron.position.x,
        neuron.position.y,
        neuron.position.z,
      )
      this.neuronMesh.setMatrixAt(i, matrix)

      // Set initial color
      color.setHex(this.params.neuronColor)
      this.neuronMesh.setColorAt(i, color)
    }

    this.neuronMesh.instanceMatrix.needsUpdate = true
    if (this.neuronMesh.instanceColor)
      this.neuronMesh.instanceColor.needsUpdate = true

    this.scene.add(this.neuronMesh)

    // Neuron bodies created successfully
  }

  public createDendrites(): void {
    // Create dendritic connections

    // Remove existing meshes
    if (this.dendriteMesh) this.scene.remove(this.dendriteMesh)

    const dendriteGroup = new this.THREE.Group()

    // Create enhanced lines for dendrites with glow capability
    for (const neuron of this.network.neurons) {
      // Create dendritic branches with subtle glow
      for (const branch of neuron.dendrites.branches) {
        if (branch.points.length >= 2) {
          this.createEnhancedLine(
            branch.points[0],
            branch.points[branch.points.length - 1],
            dendriteGroup,
            false,
            this.params.dendriteColor,
          )
        }
      }

      // Create axon connections with different styling
      for (const connection of neuron.connections) {
        const targetNeuron = this.network.getNeuronById(
          connection.targetNeuronId,
        )
        if (targetNeuron) {
          this.createEnhancedLine(
            neuron.position,
            targetNeuron.position,
            dendriteGroup,
            true,
            this.params.dendriteColor,
          )
        }
      }
    }

    this.dendriteMesh = dendriteGroup
    this.scene.add(this.dendriteMesh)

    // Dendritic connections created
  }

  private createEnhancedLine(
    start: any,
    end: any,
    group: any,
    isAxon: boolean = false,
    dendriteColor: number = 0x223355,
  ): void {
    // Create optimized tube geometry with reduced polygon count for performance
    const curve = new this.THREE.LineCurve3(start, end)
    const tubeGeometry = new this.THREE.TubeGeometry(
      curve,
      4, // Reduced segments along the curve for performance (was 8)
      isAxon ? 0.3 : 0.15, // radius - thicker for axons
      4, // Reduced radial segments for performance (was 6)
      false,
    )

    // Enhanced materials with proper lighting for depth
    const material = new this.THREE.MeshLambertMaterial({
      color: isAxon ? 0x3366aa : dendriteColor,
      transparent: true,
      opacity: isAxon ? 0.8 : 0.6,
      fog: true, // Enable fog response
      emissive: isAxon ? 0x001144 : 0x000822,
      emissiveIntensity: 0.2,
    })

    const tube = new this.THREE.Mesh(tubeGeometry, material)
    tube.userData = { isAxon, startPos: start, endPos: end }
    group.add(tube)
  }

  public createActivationPulses(): void {
    // Simple bloom-based activation system
    // Activation system initialized
  }

  public updatePulse(pulse: any): void {
    // Simple pulse update - just mark as completed
    pulse.completed = true
  }

  public removePulse(pulse: any): void {
    // Simple pulse removal
    pulse.completed = true
  }

  public updateVisuals(elapsedTime: number): void {
    // Proper neural propagation system
    if (this.network && this.network.neurons) {
      this.neuralSimulation?.propagateNeuralSignals(elapsedTime)
    }

    // Check if neuron count has changed, and re-create neuron bodies if necessary
    if (
      this.neuronMesh &&
      this.network.neurons.length !== this.neuronMesh.count
    ) {
      this.createNeuronBodies()
    }

    if (this.neuronMesh && this.neuronMesh.instanceColor) {
      const color = new this.THREE.Color()
      let activeCount = 0

      // Update each neuron with ultra-dramatic slow-wave activation colors
      for (let i = 0; i < this.network.neurons.length; i++) {
        const neuron = this.network.neurons[i]

        if (neuron.activation > 0.001) {
          // Ultra-low threshold for long-lasting waves
          activeCount++

          const activation = Math.min(neuron.activation, 1.0)

          // Extremely dramatic colors that scale with activation intensity
          const intensity = Math.pow(activation, 0.7) // Power curve for better visibility
          const baseR = 0.1 + intensity * 0.4
          const baseG = 0.2 + intensity * 0.6
          const baseB = 0.4 + intensity * 0.8

          // Blazing colors for maximum bloom impact
          color.setRGB(
            baseR + intensity * 6.0, // Ultra-intense red
            baseG + intensity * 7.0, // Blazing green
            baseB + intensity * 8.0, // Nuclear blue for dramatic bloom
          )
          this.neuronMesh.setColorAt(i, color)
        } else {
          // Inactive neuron - very subtle base with slight variation
          const baseVariation =
            (this.hashString(neuron.id || i.toString()) % 100) / 1000
          color.setRGB(
            0.15 + baseVariation,
            0.25 + baseVariation,
            0.45 + baseVariation,
          )
          this.neuronMesh.setColorAt(i, color)
        }
      }

      // Update the instance color buffer
      this.neuronMesh.instanceColor.needsUpdate = true

      if (activeCount > 0) {
        // Track current propagation activity
      }
    }

    // Update atmospheric effects
    this.visualEffects?.updateAtmosphericEffects(elapsedTime, this.params)

    // Update connection glow effects
    this.visualEffects?.updateConnectionGlow(this.dendriteMesh, elapsedTime)
  }

  /**
   * Update camera position and orientation using camera controller
   */
  public updateCamera(elapsedTime: number): void {
    this.cameraController?.updateCamera(elapsedTime)
  }

  public updateBloomPass(
    strength: number,
    radius: number,
    threshold: number,
  ): void {
    if (this.bloomPass) {
      this.bloomPass.strength = strength
      this.bloomPass.radius = radius
      this.bloomPass.threshold = threshold
    }
  }

  public render(): void {
    // Use post-processing with bloom AND built-in fog
    if (this.composer) {
      this.composer.render()
    } else {
      this.renderer.render(this.scene, this.camera)
    }
  }

  public onWindowResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(window.innerWidth, window.innerHeight)

    if (this.composer) {
      this.composer.setSize(window.innerWidth, window.innerHeight)
    }
  }

  public dispose(): void {
    if (this.renderer) {
      this.renderer.dispose()
    }
    if (this.composer) {
      this.composer.dispose()
    }
    if (this.cleanupMouseEvents) {
      this.cleanupMouseEvents()
    }
    if (this.cameraController) {
      this.cameraController.dispose()
    }
  }
}
